/**
 * 직원 재직 상태 상수
 *
 * 모든 직원 status 값은 이 상수를 사용해야 합니다.
 * literal 문자열 직접 사용 금지.
 *
 * **⚠️ 중요 변경사항:**
 * - 기존: LEAVE → 변경: ON_LEAVE
 * - 기존: INACTIVE → 변경: TERMINATED
 * - status는 employmentHistories 기반으로 자동 계산됩니다.
 *
 * @example
 * import { EMPLOYEE_STATUS } from './constants/employeeStatus';
 *
 * const employee: Employee = {
 *   status: EMPLOYEE_STATUS.ACTIVE,  // ✅
 * };
 *
 * // ❌ 잘못된 사용
 * employee.status = 'active';
 */
export const EMPLOYEE_STATUS = {
  /**
   * 재직
   *
   * 현재 고용 이력이 있고 휴직 중이 아닌 직원
   */
  ACTIVE: 'active' as const,

  /**
   * 휴직
   *
   * 현재 고용 이력이 있지만 휴직 중인 직원
   * ⚠️ 향후 구현 예정 (Phase 2)
   */
  ON_LEAVE: 'on_leave' as const,

  /**
   * 퇴사
   *
   * 현재 고용 이력이 없는 직원 (퇴사 처리됨)
   */
  TERMINATED: 'terminated' as const,
} as const;

/**
 * EmployeeStatus 타입
 *
 * EMPLOYEE_STATUS 객체의 값 타입입니다.
 */
export type EmployeeStatus = (typeof EMPLOYEE_STATUS)[keyof typeof EMPLOYEE_STATUS];

/**
 * 직원 재직 상태 라벨 (UI 표시용)
 *
 * Badge, Select 옵션 등에서 사용합니다.
 *
 * @example
 * import { EMPLOYEE_STATUS_LABELS } from './constants/employeeStatus';
 *
 * <Badge>{EMPLOYEE_STATUS_LABELS[employee.status]}</Badge>
 * // '재직', '휴직', '퇴사'
 */
export const EMPLOYEE_STATUS_LABELS: Record<EmployeeStatus, string> = {
  [EMPLOYEE_STATUS.ACTIVE]: '재직',
  [EMPLOYEE_STATUS.ON_LEAVE]: '휴직',
  [EMPLOYEE_STATUS.TERMINATED]: '퇴사',
};

/**
 * 직원 재직 상태 색상 (UI 표시용)
 *
 * Badge, Tag 등의 색상 지정에 사용합니다.
 *
 * @example
 * import { EMPLOYEE_STATUS_COLORS } from './constants/employeeStatus';
 *
 * <Badge color={EMPLOYEE_STATUS_COLORS[employee.status]}>
 *   {EMPLOYEE_STATUS_LABELS[employee.status]}
 * </Badge>
 */
export const EMPLOYEE_STATUS_COLORS: Record<EmployeeStatus, string> = {
  [EMPLOYEE_STATUS.ACTIVE]: 'green',
  [EMPLOYEE_STATUS.ON_LEAVE]: 'orange',
  [EMPLOYEE_STATUS.TERMINATED]: 'gray',
};

/**
 * 직원 재직 상태 아이콘 (UI 표시용)
 */
export const EMPLOYEE_STATUS_ICONS: Record<EmployeeStatus, string> = {
  [EMPLOYEE_STATUS.ACTIVE]: '●',
  [EMPLOYEE_STATUS.ON_LEAVE]: '■',
  [EMPLOYEE_STATUS.TERMINATED]: '○',
};

/**
 * 재직 상태 옵션 (Select 컴포넌트용)
 *
 * @example
 * import { EMPLOYEE_STATUS_OPTIONS } from './constants/employeeStatus';
 *
 * <Select options={EMPLOYEE_STATUS_OPTIONS} />
 */
export const EMPLOYEE_STATUS_OPTIONS = [
  { value: EMPLOYEE_STATUS.ACTIVE, label: EMPLOYEE_STATUS_LABELS[EMPLOYEE_STATUS.ACTIVE] },
  { value: EMPLOYEE_STATUS.ON_LEAVE, label: EMPLOYEE_STATUS_LABELS[EMPLOYEE_STATUS.ON_LEAVE] },
  { value: EMPLOYEE_STATUS.TERMINATED, label: EMPLOYEE_STATUS_LABELS[EMPLOYEE_STATUS.TERMINATED] },
];
